/**
 * reliabilityTone
 *
 * The colour of a failure share on the reliability screen: a ramp from the
 * panel's neutral grey at 0% to burgundy at 100%, with a dusty rose in the
 * middle.
 *
 * **Why this is not `failureTone`.** That ramp — the one on the request badge —
 * saturates at 10%, and deliberately: it was built for a single window on a
 * single card, where above ten percent the question stops being "how bad is it"
 * and becomes "something is broken", and a colour that keeps darkening stops
 * adding information. Reusing it here would paint every calendar at or above ten
 * percent the same burgundy, which on a screen whose whole purpose is ranking
 * forty feeds against each other throws away the ranking. Two questions, two
 * ramps; the shared part — `failureRate`, `formatFailureShare` — is imported
 * rather than copied.
 *
 * **Why the ramp is not built around the alert thresholds.** A project will be
 * able to set its own warning and error percentages, so a colour that hardcoded
 * 30 and 80 would misdescribe every project that chose differently. The ramp
 * describes the measurement; the thresholds will be drawn as marks on top of it
 * once a project has them.
 *
 * The stops are literal colours for the same reason the badge's are: the ramp is
 * computed, so a stylesheet cannot swap it.
 */

/**
 * The intermediate stops. The midpoint is a dusty rose rather than a straight
 * interpolation of grey to burgundy, which passes through a muddy green-grey —
 * the same reasoning as the badge ramp, stretched over the range this screen
 * actually shows.
 */
const STOPS = [
  { at: 0, rgb: [154, 160, 171] }, // --text-muted
  { at: 0.25, rgb: [186, 138, 118] }, // warm sand: a quarter of requests failing
  { at: 0.6, rgb: [168, 107, 122] }, // dusty rose
  { at: 1, rgb: [123, 31, 43] }, // burgundy
];

/** reliabilityTone maps a share in 0..1 onto the ramp's CSS colour. */
export function reliabilityTone(rate) {
  const value = Number(rate);
  const share = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;

  let rgb = STOPS[0].rgb;
  for (let index = 1; index < STOPS.length; index++) {
    const upper = STOPS[index];
    const lower = STOPS[index - 1];
    if (share <= upper.at) {
      const span = upper.at - lower.at;
      const t = span === 0 ? 0 : (share - lower.at) / span;
      rgb = lower.rgb.map((channel, i) => Math.round(channel + (upper.rgb[i] - channel) * t));
      break;
    }
    rgb = upper.rgb;
  }
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

/**
 * reliabilityWidth is the bar's fill as a percentage of the track.
 *
 * It exists so the caller does not do the arithmetic in JSX: the bar is the one
 * place a share becomes a length, and a share that is not a number has to become
 * zero there rather than `NaN%`, which React would render as no bar at all and a
 * reader would take for "no failures".
 */
export function reliabilityWidth(rate) {
  const value = Number(rate);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(value, 1) * 100;
}
